import { selectOnboarding, type OnboardingView } from '@doneverse/data/utils'
import { useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { ONBOARDING_FLAG } from '@/lib/onboardingLock'

/**
 * The founder's onboarding state for the tracker (ClickUp 86d3uknr2).
 *
 * Reads the profile the auth shell already holds, so the tracker costs no extra
 * request and can never disagree with the profile the rest of the app sees —
 * including under admin impersonation, where the shell keys the profile query
 * by the impersonated user.
 *
 * The rollout flag is folded in HERE rather than at each consumer: `mode` and
 * `isLocked` are meant to be the whole answer to "what does this founder see",
 * and a view that says `locked` to someone who cannot see the unlock UI is a
 * trap waiting for the second consumer.
 *
 * Webhook-driven steps (DocuSeal, Calendly) move this within seconds without a
 * reload: their transitions insert a notification, which the realtime socket
 * turns into a profile-query invalidation.
 */
export function useOnboarding(): OnboardingView {
  const { profile } = useAuth()
  const { hasFlag } = useFeatureFlags()
  const enabled = hasFlag(ONBOARDING_FLAG)
  return useMemo(() => selectOnboarding(enabled ? profile : null), [profile, enabled])
}
